import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { listDocumentMeta } from '../lib/docMeta';
import { hasNote, monthGrid, sameDay } from '../lib/calendar';
import { loadPeriodicSettings, type Period } from '../lib/periodicNotes';
import { openPeriodicNote } from '../lib/openPeriodicNote';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { getActiveLocale } from '../i18n';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';

/**
 * A month of daily notes, with the week and month notes one click away.
 *
 * A dot marks a date that already has a note; clicking any date opens its note
 * or creates it — the same idempotent path as "open today", so the calendar can
 * never produce a second note for a day. Existence is judged by title under the
 * user's current patterns, which is also how "open today" decides.
 */
export function JournalCalendar() {
  const t = useT();
  const controller = useDocControllerContext();
  const locale = getActiveLocale();
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const titles = useMemo(() => new Set(docs.map((d) => d.title.toLowerCase())), [docs]);
  const settings = {
    daily: loadPeriodicSettings('daily'),
    weekly: loadPeriodicSettings('weekly'),
    monthly: loadPeriodicSettings('monthly'),
  };

  const weeks = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const monthStart = new Date(cursor.year, cursor.month, 1);
  const dayName = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const fullDate = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    monthStart,
  );

  const shift = (delta: number) =>
    setCursor(({ year, month }) => {
      const d = new Date(year, month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const open = (period: Period, date: Date) =>
    void openPeriodicNote(period, date, controller.open, settings[period]);
  const label = (text: string, exists: boolean) =>
    exists ? `${text}, ${t('journal.hasNote')}` : text;

  const monthHasNote = hasNote(titles, monthStart, settings.monthly);

  return (
    <div className="mb-journal">
      <div className="mb-journal-head">
        <button
          className="mb-journal-nav"
          onClick={() => shift(-1)}
          aria-label={t('journal.previousMonth')}
        >
          <Icon name="chevron-left" size={14} />
        </button>
        <button
          className={`mb-journal-month${monthHasNote ? ' has-note' : ''}`}
          onClick={() => open('monthly', monthStart)}
          aria-label={label(t('journal.openMonth', { month: monthName }), monthHasNote)}
          title={t('journal.openMonth', { month: monthName })}
        >
          {monthName}
        </button>
        <button
          className="mb-journal-nav"
          onClick={() => shift(1)}
          aria-label={t('journal.nextMonth')}
        >
          <Icon name="chevron-right" size={14} />
        </button>
      </div>

      <table className="mb-journal-grid">
        <thead>
          <tr>
            <th scope="col">
              <abbr title={t('journal.week')}>{t('journal.weekShort')}</abbr>
            </th>
            {weeks[0]?.days.map(({ date }) => (
              <th key={date.getDay()} scope="col">
                {dayName.format(date).slice(0, 2)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map(({ week, monday, days }) => {
            const weekHasNote = hasNote(titles, monday, settings.weekly);
            return (
              <tr key={monday.getTime()}>
                <th scope="row">
                  <button
                    className={`mb-journal-week${weekHasNote ? ' has-note' : ''}`}
                    onClick={() => open('weekly', monday)}
                    aria-label={label(t('journal.openWeek', { week }), weekHasNote)}
                  >
                    {week}
                  </button>
                </th>
                {days.map(({ date, inMonth }) => {
                  const exists = hasNote(titles, date, settings.daily);
                  const isToday = sameDay(date, today);
                  const classes = ['mb-journal-day'];
                  if (!inMonth) classes.push('outside');
                  if (exists) classes.push('has-note');
                  if (isToday) classes.push('today');
                  return (
                    <td key={date.getTime()}>
                      <button
                        className={classes.join(' ')}
                        onClick={() => open('daily', date)}
                        aria-label={label(fullDate.format(date), exists)}
                        aria-current={isToday ? 'date' : undefined}
                      >
                        {date.getDate()}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <button
        className="mb-journal-today"
        onClick={() => {
          setCursor({ year: today.getFullYear(), month: today.getMonth() });
          open('daily', today);
        }}
      >
        <Icon name="calendar" size={13} />
        {t('journal.today')}
      </button>
    </div>
  );
}
